import request from './request'
import { getMockCouponDetailResponse, shouldUseCouponMock } from './couponCenterMock'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface CouponDetailParams {
  coupon_id: string
}

export interface CouponDetailData {
  id?: number
  coupon_id: string
  name: string
  code: string
  picture_url?: string
  discount_type?: string
  discount_type_text?: string
  discount_value?: number
  discount_text?: string
  min_spend_required?: number
  min_spend_amount?: number
  min_spend_text?: string
  start_mode?: string
  start_time?: string
  end_mode?: string
  end_time?: string
  validity_period_text?: string
  eligible_services?: string[]
  eligible_services_text?: string
  usage_limit_type?: string
  usage_limit_text?: string
  eligible_regions?: string
  eligible_regions_text?: string
  total_redemptions_unlimited?: number
  total_redemptions_limit?: number
  total_redemptions_used?: number
  redemptions_text?: string
  visibility?: string
  visibility_text?: string
  visible_to?: string
  specified_users_file?: string
  availability?: string
  availability_text?: string
  description?: string
  [key: string]: unknown
}

export type CouponDetailVisibilityTone = 'visible' | 'hidden'
export type CouponDetailAvailabilityTone = 'active' | 'inactive'

export interface CouponDetailView {
  couponId: string
  name: string
  code: string
  pictureUrl: string
  discountType: string
  discountAmount: string
  minSpend: string
  validityPeriod: string
  eligibleServices: string
  usageLimit: string
  eligibleRegions: string
  totalRedemptions: string
  visibility: string
  visibilityTone: CouponDetailVisibilityTone
  availability: string
  availabilityTone: CouponDetailAvailabilityTone
  description: string
}

function normalizeCouponAssetPath(path?: string): string {
  return (path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/{2,}/g, '/')
}

/**
 * 与技师详情 `getAvatarUrl` 一致：http 直用，否则 VITE_BASE_URL + path。
 * 优惠券 upload 存的是 `uploads/admin/...`（无前导 /），公网需 `/storage/` 前缀。
 */
export function resolveCouponPictureUrl(path?: string): string {
  const raw = normalizeCouponAssetPath(path)
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw) || raw.startsWith('blob:') || raw.startsWith('data:')) {
    return raw
  }

  let pathname = raw.startsWith('/') ? raw : `/${raw}`
  if (!pathname.startsWith('/storage/') && pathname.startsWith('/uploads/')) {
    pathname = `/storage${pathname}`
  }

  const base = (import.meta.env.VITE_BASE_URL || '').replace(/\/$/, '')
  return `${base}${pathname}`
}

function readCouponPictureRaw(data: CouponDetailData): string {
  const keys = [
    'picture_url',
    'picture',
    'image_url',
    'image',
    'photo_url',
    'photo',
    'picture_path',
    'image_path',
  ] as const

  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string' && value.trim()) return normalizeCouponAssetPath(value)
  }
  return ''
}

function resolveVisibilityTone(visibility?: string): CouponDetailVisibilityTone {
  const value = (visibility ?? '').toLowerCase()
  if (value === 'visible' || value === '1') return 'visible'
  if (value === 'hidden' || value === '0') return 'hidden'
  return value.includes('visible') ? 'visible' : 'hidden'
}

function resolveAvailabilityTone(availability?: string): CouponDetailAvailabilityTone {
  const value = (availability ?? '').toLowerCase()
  if (value === 'active' || value === '1') return 'active'
  if (value === 'inactive' || value === '0') return 'inactive'
  return value.includes('active') ? 'active' : 'inactive'
}

function resolveUsageLimitText(data: CouponDetailData): string {
  return resolveLimitOptionLabel(data.usage_limit_type, data.usage_limit_text)
}

function resolveEligibleRegionsText(data: CouponDetailData): string {
  return resolveLimitOptionLabel(data.eligible_regions, data.eligible_regions_text)
}

/**
 * Usage Limit / Eligible Regions 共用三档文案。
 * 以后端枚举为准：eligible_regions_text 目前常错误固定为 "Unlimited"。
 */
function resolveLimitOptionLabel(raw?: string, text?: string): string {
  const value = (raw ?? '').toLowerCase().trim()
  const display = (text ?? '').trim()
  const displayLower = display.toLowerCase()

  if (value === 'one_per_address') return 'One-time use per address'
  if (value === 'no_limit' || value === 'unlimited') return 'No usage limit'
  if (value === 'one_per_account_or_address' || value === 'one_per_account') {
    return 'One-time use per account or address'
  }

  // 枚举未知时再回退文案（且忽略后端错误的固定 Unlimited）
  if (
    (displayLower.includes('one_per_address') || /per address/i.test(display)) &&
    !displayLower.includes('account')
  ) {
    return 'One-time use per address'
  }
  if (/no usage limit/i.test(display) || displayLower.includes('no_limit')) {
    return 'No usage limit'
  }
  if (
    /account or address/i.test(display) ||
    /one-time use per account/i.test(display)
  ) {
    return 'One-time use per account or address'
  }
  // 仅文案为 Unlimited、无可靠枚举时，按第三档展示
  if (displayLower === 'unlimited') return 'No usage limit'
  return display || 'One-time use per account or address'
}

function resolveDiscountAmount(data: CouponDetailData): string {
  if (data.discount_text?.trim()) {
    const match = data.discount_text.match(/(\$[\d.,]+|\d+(?:\.\d+)?%)/)
    if (match?.[1]) return match[1]
  }
  if (typeof data.discount_value === 'number') {
    const type = (data.discount_type ?? '').toLowerCase()
    if (type.includes('percent')) return `${data.discount_value}%`
    return `$${data.discount_value.toFixed(2)}`
  }
  return data.discount_text?.trim() ?? ''
}

function resolveTotalRedemptions(data: CouponDetailData): string {
  if (data.redemptions_text?.trim()) return data.redemptions_text.trim()
  if (data.total_redemptions_unlimited === 1) return 'Unlimited'
  const used = data.total_redemptions_used
  const limit = data.total_redemptions_limit
  if (typeof used === 'number' && typeof limit === 'number') return `${used}/${limit}`
  return ''
}

function formatCouponDateTimeDisplay(value?: string): string {
  const raw = (value ?? '').trim()
  if (!raw) return ''

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  )
  if (match?.[1] && match[2] && match[3]) {
    const date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4] ?? 0),
      Number(match[5] ?? 0),
      Number(match[6] ?? 0),
    )
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      })
    }
  }

  const parsed = new Date(raw)
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  }
  return raw
}

const ELIGIBLE_SERVICE_LABELS: Record<string, string> = {
  all: 'All services',
  all_services: 'All services',
  essential_care_subscription: 'Essential Care',
  enhanced_care_subscription: 'Enhanced Care',
  premium_care_subscription: 'Premium Care',
  essential_care: 'Essential Care',
  enhanced_care: 'Enhanced Care',
  premium_care: 'Premium Care',
}

function labelForEligibleService(value: string): string {
  const key = value.trim().toLowerCase()
  if (ELIGIBLE_SERVICE_LABELS[key]) return ELIGIBLE_SERVICE_LABELS[key]
  return value
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

/** 优先用结构化 start/end 拼 Validity；文案缺开始时间时也能补上 */
export function resolveValidityPeriodText(data: CouponDetailData): string {
  const fromApi = data.validity_period_text?.trim() || ''
  const startMode = (data.start_mode ?? '').trim().toLowerCase()
  const endMode = (data.end_mode ?? '').trim().toLowerCase()
  const startTime = formatCouponDateTimeDisplay(data.start_time)
  const endTime = formatCouponDateTimeDisplay(data.end_time)

  const parts: string[] = []
  if (startMode === 'specific_time' && startTime) {
    parts.push(`Valid from ${startTime}`)
  }
  if (endMode.includes('never')) {
    parts.push('Never expires')
  } else if ((endMode === 'specific_time' || !!data.end_time) && endTime) {
    parts.push(`Until ${endTime}`)
  }

  if (parts.length) {
    // 后端文案只有 Never expires、但实际有 specific start → 用拼接结果
    if (startMode === 'specific_time' && startTime) return parts.join(', ')
    if (!fromApi) return parts.join(', ')
  }

  return fromApi || parts.join(', ')
}

/** 优先用 eligible_services[]；后端仍回 “Eligible for HM Care” 时覆盖 */
export function resolveEligibleServicesText(data: CouponDetailData): string {
  const fromApi = data.eligible_services_text?.trim() || ''
  const services = Array.isArray(data.eligible_services)
    ? data.eligible_services.map((item) => String(item).trim()).filter(Boolean)
    : []

  const isLegacyHmCare =
    /hm\s*care/i.test(fromApi) && !/essential|enhanced|premium/i.test(fromApi)

  if (services.length > 0 && (!fromApi || isLegacyHmCare)) {
    const labels = services.map(labelForEligibleService)
    if (labels.some((label) => label.toLowerCase() === 'all services')) {
      return 'All services'
    }
    return labels.join(', ')
  }

  // 后端文案若带 "Eligible for " 前缀，展示时去掉
  return fromApi.replace(/^eligible\s+for\s+/i, '').trim() || fromApi
}

export function mapCouponDetailToView(data: CouponDetailData, fallbackPicture = ''): CouponDetailView {
  const visibilityRaw = data.visibility ?? ''
  const availabilityRaw = data.availability ?? ''

  return {
    couponId: data.coupon_id,
    name: data.name ?? '',
    code: data.code ?? '',
    pictureUrl: resolveCouponPictureUrl(readCouponPictureRaw(data)) || fallbackPicture,
    discountType: data.discount_type_text?.trim() || data.discount_type?.trim() || '',
    discountAmount: resolveDiscountAmount(data),
    minSpend: data.min_spend_text?.trim() || 'Not required',
    validityPeriod: resolveValidityPeriodText(data),
    eligibleServices: resolveEligibleServicesText(data),
    usageLimit: resolveUsageLimitText(data),
    eligibleRegions: resolveEligibleRegionsText(data),
    totalRedemptions: resolveTotalRedemptions(data),
    visibility: data.visibility_text?.trim() || visibilityRaw,
    visibilityTone: resolveVisibilityTone(visibilityRaw),
    availability: data.availability_text?.trim() || availabilityRaw,
    availabilityTone: resolveAvailabilityTone(availabilityRaw),
    description: data.description?.trim() || '',
  }
}

/** POST `/api/admin/coupon/detail` */
export async function postCouponDetail(
  params: CouponDetailParams,
): Promise<ApiResponse<CouponDetailData>> {
  if (shouldUseCouponMock()) {
    return getMockCouponDetailResponse(params.coupon_id) as ApiResponse<CouponDetailData>
  }
  return request.post<ApiResponse<CouponDetailData>>('/api/admin/coupon/detail', params)
}
